import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { UserContext } from "./UserContext";
import { FlowiseChatSession } from "@/lib/flowise/types";
import type { FlowiseMessage } from "@/lib/samSessionApi";
import samSessionApi from "@/lib/samSessionApi";
import type { ChatMessage, ChatMessageChartData } from "@/types/chatMessage";
import { Conversation, generateConversationTitle } from "@/types/conversation";
import { chatStorage } from "@/utils/chatStorage";

/** Extract chart data from a Flowise message's usedTools field */
function extractChartData(
  msg: FlowiseMessage
): ChatMessageChartData | undefined {
  if (!msg.usedTools?.length) return undefined;

  const chartTool = msg.usedTools
    .map((t) => t as Record<string, unknown>)
    .find((t) => (t.name || t.tool) === "create_chart");

  if (!chartTool) return undefined;

  const rawOutput =
    chartTool.output ||
    chartTool.toolOutput ||
    (chartTool.args as Record<string, unknown>)?.toolOutput;

  if (!rawOutput) return undefined;

  try {
    const output =
      typeof rawOutput === "string" ? JSON.parse(rawOutput) : rawOutput;
    if (output?.type === "chart" && output?.query && output?.visualization) {
      return { query: output.query, visualization: output.visualization };
    }
  } catch {
    /* ignore parse errors */
  }
  return undefined;
}

interface ConversationContextType {
  conversations: Conversation[];
  currentConversationId: string | null;
  currentConversation: Conversation | null;
  createNewConversation: () => string;
  clearCurrentConversation: () => void;
  switchToConversation: (conversationId: string) => void;
  deleteConversation: (conversationId: string) => void;
  updateCurrentConversation: (
    messages: ChatMessage[],
    session: FlowiseChatSession
  ) => void;
  updateConversationById: (
    conversationId: string,
    messages: ChatMessage[],
    session: FlowiseChatSession
  ) => void;
  clearAllConversations: () => void;
  // Audit-specific methods
  findConversationByAuditId: (auditId: number) => Conversation | null;
  getOrCreateAuditConversation: (
    auditId: number,
    templateTitle: string
  ) => string;
  // Backend sync methods
  syncConversationToBackend: (
    conversationId: string,
    flowiseChatId: string
  ) => Promise<void>;
  loadConversationFromBackend: (backendSessionId: number) => Promise<void>;
  isLoadingFromBackend: boolean;
}

export const ConversationContext =
  createContext<ConversationContextType | undefined>(undefined);

export const useConversation = () => {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error(
      "useConversation must be used within a ConversationProvider"
    );
  }
  return context;
};

/**
 * Optional version of useConversation that returns undefined
 * when used outside ConversationProvider instead of throwing.
 * Use this for components that may be rendered outside the provider.
 */
export const useConversationOptional = () => {
  return useContext(ConversationContext);
};

export type { ConversationContextType };

interface ConversationProviderProps {
  children: React.ReactNode;
  enterpriseId: string;
}

export const ConversationProvider: React.FC<ConversationProviderProps> = ({
  children,
  enterpriseId,
}) => {
  const { user } = useContext(UserContext);
  const userId = user?.id;

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] =
    useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const [isLoadingFromBackend, setIsLoadingFromBackend] = useState(false);

  // Track which enterpriseId we've loaded conversations for
  // This prevents the save effect from running before load completes
  const loadedEnterpriseIdRef = useRef<string | null>(null);

  // Track which enterpriseId we've attempted backend hydration for
  // Prevents duplicate fetches when dependencies change
  const hasAttemptedBackendHydrationRef = useRef<string | null>(null);

  // Load conversations from localStorage on mount
  useEffect(() => {
    if (!enterpriseId) {
      loadedEnterpriseIdRef.current = null;
      setIsInitialized(true);
      return;
    }

    const loadedConversations = chatStorage.loadConversations(enterpriseId);
    const loadedCurrentId = chatStorage.loadCurrentConversationId(enterpriseId);

    setConversations(loadedConversations);

    // If we have a saved current conversation, use it
    if (
      loadedCurrentId &&
      loadedConversations.some((c) => c.id === loadedCurrentId)
    ) {
      setCurrentConversationId(loadedCurrentId);
    } else if (loadedConversations.length > 0) {
      // Otherwise use the most recent conversation
      setCurrentConversationId(loadedConversations[0].id);
    } else {
      // No conversations exist - leave empty (will be created on-demand when user sends message)
      setCurrentConversationId(null);
    }

    // Mark that we've loaded for this enterpriseId AFTER setting state
    // This ensures save effects don't run until load is complete
    loadedEnterpriseIdRef.current = enterpriseId;
    setIsInitialized(true);
  }, [enterpriseId]);

  // Save conversations whenever they change
  useEffect(() => {
    // Only save if we've loaded for this exact enterpriseId
    // This prevents race condition where save runs before load completes
    if (
      !isInitialized ||
      !enterpriseId ||
      loadedEnterpriseIdRef.current !== enterpriseId
    )
      return;
    chatStorage.saveConversations(enterpriseId, conversations);
  }, [conversations, isInitialized, enterpriseId]);

  // Save current conversation ID whenever it changes
  useEffect(() => {
    // Only save if we've loaded for this exact enterpriseId
    if (
      !isInitialized ||
      !enterpriseId ||
      !currentConversationId ||
      loadedEnterpriseIdRef.current !== enterpriseId
    )
      return;
    chatStorage.saveCurrentConversationId(enterpriseId, currentConversationId);
  }, [currentConversationId, isInitialized, enterpriseId]);

  // Hydrate from backend — merge missing sessions and remove deleted ones
  useEffect(() => {
    if (!isInitialized || !enterpriseId || !userId) return undefined;
    if (hasAttemptedBackendHydrationRef.current === enterpriseId)
      return undefined;

    hasAttemptedBackendHydrationRef.current = enterpriseId;
    let cancelled = false;

    const hydrate = async () => {
      setIsLoadingFromBackend(true);
      try {
        const { sessions } = await samSessionApi.listSessions(
          Number(enterpriseId),
          userId,
          { limit: 50 }
        );

        if (cancelled) return;

        const existingConvs = chatStorage.loadConversations(enterpriseId);
        const activeBackendIds = new Set(sessions.map((s) => s.id));

        // Remove local conversations whose backend session was deleted
        const deletedLocalIds = new Set(
          existingConvs
            .filter(
              (c) =>
                c.backendSessionId && !activeBackendIds.has(c.backendSessionId)
            )
            .map((c) => c.id)
        );

        // Find backend sessions missing from localStorage
        const existingBackendIds = new Set(
          existingConvs
            .filter((c) => c.backendSessionId)
            .map((c) => c.backendSessionId)
        );
        const missingSessions = sessions.filter(
          (s) => !existingBackendIds.has(s.id)
        );

        const hasChanges =
          deletedLocalIds.size > 0 || missingSessions.length > 0;
        if (cancelled || !hasChanges) return;

        const restoredConversations: Conversation[] = missingSessions.map(
          (session, index) => ({
            id: `conv_backend_${session.id}_${Date.now()}_${index}`,
            title: session.title || "Restored Conversation",
            messages: [],
            session: { chatId: session.flowiseChatId },
            createdAt: new Date(session.createdAt),
            updatedAt: new Date(session.updatedAt),
            backendSessionId: session.id,
            isSyncedToBackend: true,
            metadata: session.metadata as unknown as Conversation["metadata"],
            needsHydration: true,
          })
        );

        if (cancelled) return;

        // Merge: add missing sessions and remove deleted ones
        setConversations((prev) => {
          const pruned = prev.filter((c) => !deletedLocalIds.has(c.id));
          return [...restoredConversations, ...pruned];
        });

        // If current conversation was deleted, switch to the most recent one
        const currentId = chatStorage.loadCurrentConversationId(enterpriseId);
        const currentWasDeleted =
          currentId &&
          existingConvs.find(
            (c) => c.id === currentId && deletedLocalIds.has(c.id)
          );

        if (
          currentWasDeleted ||
          !existingConvs.some((c) => c.messages.length > 0)
        ) {
          // Pick the first restored conversation, or the first remaining one
          if (restoredConversations.length > 0) {
            setCurrentConversationId(restoredConversations[0].id);
          }
        }
      } catch (error) {
        console.error("Failed to hydrate conversations from backend:", error);
      } finally {
        if (!cancelled) {
          setIsLoadingFromBackend(false);
        }
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [isInitialized, enterpriseId, userId]);

  const currentConversation =
    conversations.find((c) => c.id === currentConversationId) || null;

  // Lazy-load messages when switching to a skeleton conversation
  useEffect(() => {
    if (
      !currentConversation?.needsHydration ||
      !currentConversation.backendSessionId ||
      !userId ||
      !enterpriseId
    )
      return undefined;

    const { id: convId, backendSessionId } = currentConversation;
    let cancelled = false;

    const hydrateMessages = async () => {
      try {
        const { messages: flowiseMessages } = await samSessionApi.getMessages(
          Number(enterpriseId),
          userId,
          backendSessionId,
          { order: "ASC" }
        );

        if (cancelled) return;

        const chatMessages: ChatMessage[] = flowiseMessages.map((msg) => ({
          id: msg.id,
          text: msg.content,
          sender: (msg.role === "userMessage" ? "user" : "assistant") as
            | "user"
            | "assistant",
          timestamp: new Date(msg.createdDate),
          followUpPrompts: msg.followUpPrompts,
          ...(extractChartData(msg) && { chartData: extractChartData(msg) }),
        }));

        setConversations((prev) =>
          prev.map((c) =>
            c.id === convId
              ? { ...c, messages: chatMessages, needsHydration: false }
              : c
          )
        );
      } catch (error) {
        console.error("Failed to load messages for conversation:", error);
        // Clear hydration flag on error to prevent infinite retries
        setConversations((prev) =>
          prev.map((c) =>
            c.id === convId ? { ...c, needsHydration: false } : c
          )
        );
      }
    };

    hydrateMessages();
    return () => {
      cancelled = true;
    };
  }, [
    currentConversation?.needsHydration,
    currentConversation?.id,
    currentConversation?.backendSessionId,
    userId,
    enterpriseId,
  ]);

  const createNewConversationInternal = useCallback((): string => {
    const newConversation: Conversation = {
      id: `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      title: "New Conversation",
      messages: [],
      session: {},
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    setConversations((prev) => [newConversation, ...prev]);
    return newConversation.id;
  }, []);

  const createNewConversation = useCallback((): string => {
    // If current conversation is already empty, just return its ID
    const current = conversations.find((c) => c.id === currentConversationId);
    if (current && current.messages.length === 0) {
      return current.id;
    }
    const newId = createNewConversationInternal();
    setCurrentConversationId(newId);
    return newId;
  }, [createNewConversationInternal, conversations, currentConversationId]);

  const clearCurrentConversation = useCallback(() => {
    // Remove the current conversation if it has no messages (orphan cleanup)
    if (currentConversationId) {
      setConversations((prev) => {
        const current = prev.find((c) => c.id === currentConversationId);
        if (current && current.messages.length === 0) {
          return prev.filter((c) => c.id !== currentConversationId);
        }
        return prev;
      });
    }
    setCurrentConversationId(null);
  }, [currentConversationId]);

  const switchToConversation = useCallback((conversationId: string) => {
    setCurrentConversationId(conversationId);
  }, []);

  const deleteConversation = useCallback(
    (conversationId: string) => {
      // Find the conversation to check if it has a backend session
      const convToDelete = conversations.find((c) => c.id === conversationId);

      // Delete from backend if synced (fire and forget - don't block on this)
      if (convToDelete?.backendSessionId && userId && enterpriseId) {
        samSessionApi
          .deleteSession(
            Number(enterpriseId),
            userId,
            convToDelete.backendSessionId
          )
          .catch((error) => {
            console.error("Failed to delete session from backend:", error);
          });
      }

      setConversations((prev) => {
        const filtered = prev.filter((c) => c.id !== conversationId);

        // If we deleted the current conversation, switch to another one or leave empty
        if (conversationId === currentConversationId) {
          if (filtered.length > 0) {
            setCurrentConversationId(filtered[0].id);
          } else {
            // No conversations left - set to null (conversation will be created on-demand when user sends message)
            setCurrentConversationId(null);
          }
        }

        return filtered;
      });
    },
    [currentConversationId, conversations, userId, enterpriseId]
  );

  const updateConversationById = useCallback(
    (
      conversationId: string,
      messages: ChatMessage[],
      session: FlowiseChatSession
    ) => {
      setConversations((prev) => {
        // Update the specific conversation by ID
        const updated = prev.map((conv) => {
          if (conv.id === conversationId) {
            // For audit conversations, preserve the audit title
            // For regular conversations, regenerate title from messages (uses last user message)
            let title: string;
            if (conv.metadata?.type === "audit") {
              title = conv.title; // Keep existing audit title (e.g., "Audit: Water Management")
            } else if (messages.length > 0) {
              title = generateConversationTitle(messages);
            } else {
              title = "New Conversation";
            }

            // Only update timestamp if messages have actually changed
            const hasNewMessages =
              messages.length !== conv.messages.length ||
              messages.some(
                (msg, idx) =>
                  !conv.messages[idx] || msg.text !== conv.messages[idx].text
              );

            return {
              ...conv,
              messages,
              session,
              title,
              // Only update timestamp when messages actually change (not just when viewing)
              updatedAt: hasNewMessages ? new Date() : conv.updatedAt,
            };
          }
          return conv;
        });

        // Keep conversations in creation order (newest first) - don't reorder on updates
        // This preserves the chronological history order
        return updated;
      });
    },
    []
  );

  const updateCurrentConversation = useCallback(
    (messages: ChatMessage[], session: FlowiseChatSession) => {
      if (!currentConversationId) return;
      updateConversationById(currentConversationId, messages, session);
    },
    [currentConversationId, updateConversationById]
  );

  const clearAllConversations = useCallback(() => {
    setConversations([]);
    const newId = createNewConversationInternal();
    setCurrentConversationId(newId);
    if (enterpriseId) {
      chatStorage.clearAllConversations(enterpriseId);
    }
  }, [enterpriseId, createNewConversationInternal]);

  // Find existing conversation for a specific audit
  const findConversationByAuditId = useCallback(
    (auditId: number): Conversation | null => {
      return (
        conversations.find(
          (c) => c.metadata?.type === "audit" && c.metadata?.auditId === auditId
        ) || null
      );
    },
    [conversations]
  );

  // Get existing or create new conversation for an audit
  const getOrCreateAuditConversation = useCallback(
    (auditId: number, templateTitle: string): string => {
      // First check if conversation already exists for this audit
      const existing = findConversationByAuditId(auditId);
      if (existing) {
        // Switch to existing conversation
        setCurrentConversationId(existing.id);
        return existing.id;
      }

      // Create new conversation with audit metadata
      const newConversation: Conversation = {
        id: `conv_audit_${auditId}_${Date.now()}`,
        title: `Audit: ${templateTitle}`,
        messages: [],
        session: {},
        createdAt: new Date(),
        updatedAt: new Date(),
        metadata: {
          type: "audit",
          auditId,
          templateTitle,
        },
      };

      setConversations((prev) => [newConversation, ...prev]);
      setCurrentConversationId(newConversation.id);
      return newConversation.id;
    },
    [findConversationByAuditId]
  );

  // Sync a local conversation to the backend
  const syncConversationToBackend = useCallback(
    async (conversationId: string, flowiseChatId: string): Promise<void> => {
      if (!userId || !enterpriseId) {
        console.warn("Cannot sync to backend: missing userId or enterpriseId");
        return;
      }

      const conversation = conversations.find((c) => c.id === conversationId);
      if (!conversation) {
        console.warn("Conversation not found:", conversationId);
        return;
      }

      // Skip if already synced
      if (conversation.backendSessionId) {
        return;
      }

      try {
        // Generate title from messages to avoid race condition where conversation.title
        // might still be "New Conversation" due to async state updates
        const title =
          conversation.metadata?.type === "audit"
            ? conversation.title // Keep audit titles as-is
            : generateConversationTitle(conversation.messages);

        const backendSession = await samSessionApi.createSession(
          Number(enterpriseId),
          userId,
          {
            flowiseChatId,
            title,
            metadata: conversation.metadata
              ? { ...conversation.metadata }
              : undefined,
          }
        );

        // Update local conversation with backend session ID
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId
              ? {
                  ...conv,
                  backendSessionId: backendSession.id,
                  isSyncedToBackend: true,
                }
              : conv
          )
        );
      } catch (error) {
        console.error("Failed to sync conversation to backend:", error);
        // Don't throw - keep local conversation working even if backend sync fails
      }
    },
    [userId, enterpriseId, conversations]
  );

  // Load a conversation from the backend (for session resume)
  const loadConversationFromBackend = useCallback(
    async (backendSessionId: number): Promise<void> => {
      if (!userId || !enterpriseId) {
        console.warn(
          "Cannot load from backend: missing userId or enterpriseId"
        );
        return;
      }

      try {
        // Get session details
        const session = await samSessionApi.getSession(
          Number(enterpriseId),
          userId,
          backendSessionId
        );

        // Get messages from Flowise via core-api proxy
        const { messages: flowiseMessages } = await samSessionApi.getMessages(
          Number(enterpriseId),
          userId,
          backendSessionId,
          { order: "ASC" }
        );

        // Convert Flowise messages to local format
        const chatMessages: ChatMessage[] = flowiseMessages.map((msg) => ({
          id: msg.id,
          text: msg.content,
          sender: msg.role === "userMessage" ? "user" : "assistant",
          timestamp: new Date(msg.createdDate),
          followUpPrompts: msg.followUpPrompts,
          artifacts: msg.artifacts,
          ...(extractChartData(msg) && { chartData: extractChartData(msg) }),
        }));

        // Check if we already have this conversation locally
        const existingConv = conversations.find(
          (c) => c.backendSessionId === backendSessionId
        );

        if (existingConv) {
          // Update existing conversation with latest messages
          setConversations((prev) =>
            prev.map((conv) =>
              conv.id === existingConv.id
                ? {
                    ...conv,
                    messages: chatMessages,
                    session: { chatId: session.flowiseChatId },
                    updatedAt: new Date(),
                  }
                : conv
            )
          );
          setCurrentConversationId(existingConv.id);
        } else {
          // Create new local conversation from backend session
          const newConversation: Conversation = {
            id: `conv_backend_${backendSessionId}_${Date.now()}`,
            title: session.title || "Restored Conversation",
            messages: chatMessages,
            session: { chatId: session.flowiseChatId },
            createdAt: new Date(session.createdAt),
            updatedAt: new Date(session.updatedAt),
            backendSessionId: session.id,
            isSyncedToBackend: true,
            metadata: session.metadata as unknown as Conversation["metadata"],
          };

          setConversations((prev) => [newConversation, ...prev]);
          setCurrentConversationId(newConversation.id);
        }
      } catch (error) {
        console.error("Failed to load conversation from backend:", error);
        throw error;
      }
    },
    [userId, enterpriseId, conversations]
  );

  const contextValue = useMemo(
    () => ({
      conversations,
      currentConversationId,
      currentConversation,
      createNewConversation,
      clearCurrentConversation,
      switchToConversation,
      deleteConversation,
      updateCurrentConversation,
      updateConversationById,
      clearAllConversations,
      findConversationByAuditId,
      getOrCreateAuditConversation,
      syncConversationToBackend,
      loadConversationFromBackend,
      isLoadingFromBackend,
    }),
    [
      conversations,
      currentConversationId,
      currentConversation,
      createNewConversation,
      clearCurrentConversation,
      switchToConversation,
      deleteConversation,
      updateCurrentConversation,
      updateConversationById,
      clearAllConversations,
      findConversationByAuditId,
      getOrCreateAuditConversation,
      syncConversationToBackend,
      loadConversationFromBackend,
      isLoadingFromBackend,
    ]
  );

  return (
    <ConversationContext.Provider value={contextValue}>
      {children}
    </ConversationContext.Provider>
  );
};
